// The MapLibre stack: paper, density, neighbourhoods, faded marks, halos,
// clusters, marks, labels, the reader. No DOM marker is ever created; every
// church is a feature in a GeoJSON source and the GPU draws it.

import { PAPER, INK, RED, tint, registerMarks, repaint, KIND_PRIORITY } from './marks.js';

const BASEMAP = 'https://tiles.openfreemap.org/styles/positron';
const FONT = ['Noto Sans Regular'];
const FONT_BOLD = ['Noto Sans Bold'];
const EMPTY = { type: 'FeatureCollection', features: [] };

export function toFeature(c) {
  return { type: 'Feature', id: c.i, properties: { i: c.i, n: c.n, k: c.k, pr: KIND_PRIORITY[c.k] || 5, hc: c.hc ? 1 : 0, ha: c.ha ? 1 : 0, sp: c.l && c.l.includes('spa') ? 1 : 0, isC: c.k === 'c' ? 1 : 0, isB: c.k === 'b' ? 1 : 0, isS: c.k === 's' ? 1 : 0 }, geometry: { type: 'Point', coordinates: [c.lng, c.lat] } };
}

const COUNT = ['coalesce', ['get', 'point_count'], ['get', 'n']];
const CLUSTER_RADIUS = ['step', COUNT, 13, 10, 16, 50, 19, 200, 23, 1000, 27];

function clusterLayers(source, prefix, filter) {
  const f = filter ? { filter } : {};
  return [
    { id: prefix + '-halo', type: 'circle', source, ...f, paint: { 'circle-radius': ['+', CLUSTER_RADIUS, 5], 'circle-color': RED, 'circle-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.16, 0.06] } },
    { id: prefix, type: 'circle', source, ...f, paint: { 'circle-radius': CLUSTER_RADIUS, 'circle-color': PAPER, 'circle-stroke-color': RED, 'circle-stroke-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2.5, 1.5] } },
    { id: prefix + '-count', type: 'symbol', source, ...f, layout: { 'text-field': ['to-string', COUNT], 'text-font': FONT_BOLD, 'text-size': ['step', COUNT, 11, 50, 12, 1000, 13], 'text-allow-overlap': true, 'text-ignore-placement': true }, paint: { 'text-color': RED } },
  ];
}

export function createMap(el, opts) {
  const { center, zoom, onReady } = opts;
  const map = new maplibregl.Map({
    container: el, center, zoom, minZoom: 1, maxZoom: 18, attributionControl: false,
    style: { version: 8, sources: {}, layers: [{ id: 'bg', type: 'background', paint: { 'background-color': tint(0.045) } }] },
    fadeDuration: 150, hash: false, pitchWithRotate: false, dragRotate: false, touchPitch: false,
  });
  map.touchZoomRotate.disableRotation();
  map.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: '<a href="https://openfreemap.org">OpenFreeMap</a> · <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' }), 'bottom-right');

  const api = { map, hovered: null, selected: null, listeners: {}, labels: true, heat: false };
  api.on = (ev, fn) => { (api.listeners[ev] ||= []).push(fn); };
  const emit = (ev, ...a) => (api.listeners[ev] || []).forEach((fn) => fn(...a));

  fetch(BASEMAP).then((r) => r.json()).then((style) => {
    map.setStyle(repaint(style), { diff: false });
    map.once('styledata', () => { addOverlay(map, api, emit); if (onReady) onReady(api); });
  }).catch(() => { addOverlay(map, api, emit); if (onReady) onReady(api); });
  return api;
}

function addOverlay(map, api, emit) {
  if (map.getSource('churches')) return;
  registerMarks(map);
  map.addSource('churches', { type: 'geojson', data: EMPTY, promoteId: 'i', cluster: true, clusterRadius: 46, clusterMaxZoom: 13,
    clusterProperties: { hc: ['+', ['get', 'hc']], ha: ['+', ['get', 'ha']], sp: ['+', ['get', 'sp']], isC: ['+', ['get', 'isC']], isB: ['+', ['get', 'isB']], isS: ['+', ['get', 'isS']] } });
  map.addSource('dim', { type: 'geojson', data: EMPTY, promoteId: 'i' });
  map.addSource('grid', { type: 'geojson', data: EMPTY, generateId: true });
  map.addSource('you', { type: 'geojson', data: EMPTY });
  map.addSource('nb', { type: 'geojson', data: EMPTY, promoteId: 'slug' });

  const isCluster = ['has', 'point_count'];
  const isPoint = ['!', isCluster];
  const layers = [
    { id: 'heat', type: 'heatmap', source: 'churches', layout: { visibility: 'none' }, paint: {
      'heatmap-weight': ['coalesce', ['get', 'point_count'], 1], 'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 12, 1.4],
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 6, 14, 12, 36], 'heatmap-opacity': 0.55,
      'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(187,59,55,0)', 0.2, 'rgba(187,59,55,0.18)', 0.5, 'rgba(187,59,55,0.42)', 1, 'rgba(187,59,55,0.8)'] } },
    { id: 'nb-fill', type: 'fill', source: 'nb', paint: { 'fill-color': RED, 'fill-opacity': ['case', ['boolean', ['feature-state', 'active'], false], 0.07, 0] } },
    { id: 'nb-line', type: 'line', source: 'nb', paint: { 'line-color': ['case', ['boolean', ['feature-state', 'active'], false], RED, tint(0.3)], 'line-width': ['case', ['boolean', ['feature-state', 'active'], false], 1.6, 0.6], 'line-dasharray': [3, 2] } },
    { id: 'dim', type: 'symbol', source: 'dim', minzoom: 9, layout: { 'icon-image': ['concat', 'mk-', ['get', 'k'], '-dim'], 'icon-allow-overlap': true, 'icon-ignore-placement': true, 'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.7, 13, 1] } },
    ...clusterLayers('grid', 'grid', null),
    { id: 'halo-selected', type: 'circle', source: 'churches', filter: isPoint, paint: { 'circle-radius': ['case', ['boolean', ['feature-state', 'selected'], false], 18, 0], 'circle-color': RED, 'circle-opacity': 0.14, 'circle-stroke-color': RED, 'circle-stroke-width': ['case', ['boolean', ['feature-state', 'selected'], false], 1.5, 0], 'circle-stroke-opacity': 0.9, 'circle-radius-transition': { duration: 180 } } },
    { id: 'halo-hover', type: 'circle', source: 'churches', filter: isPoint, paint: { 'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 14, 0], 'circle-color': RED, 'circle-opacity': 0.12, 'circle-radius-transition': { duration: 120 } } },
    ...clusterLayers('churches', 'clusters', isCluster),
    { id: 'points', type: 'symbol', source: 'churches', filter: isPoint, layout: { 'icon-image': ['concat', 'mk-', ['get', 'k']], 'icon-allow-overlap': true, 'icon-ignore-placement': true, 'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.75, 12, 1], 'symbol-sort-key': ['get', 'pr'] } },
    { id: 'labels', type: 'symbol', source: 'churches', filter: isPoint, minzoom: 11, layout: {
      'text-field': ['get', 'n'], 'text-font': FONT, 'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10.5, 15, 12.5], 'text-max-width': 9,
      'text-variable-anchor': ['top', 'bottom', 'left', 'right'], 'text-radial-offset': ['match', ['get', 'k'], 'c', 1.1, 'b', 0.95, 's', 0.95, 0.75], 'text-justify': 'auto',
      'text-optional': false, 'text-padding': 3, 'symbol-sort-key': ['get', 'pr'],
      'text-letter-spacing': 0.01 }, paint: { 'text-color': INK, 'text-halo-color': PAPER, 'text-halo-width': 1.4, 'text-halo-blur': 0.2, 'text-opacity': ['interpolate', ['linear'], ['zoom'], 11, ['case', ['<=', ['get', 'pr'], 4], 1, 0], 12.5, 1] } },
    { id: 'you-acc', type: 'circle', source: 'you', paint: { 'circle-radius': ['get', 'px'], 'circle-color': INK, 'circle-opacity': 0.06, 'circle-stroke-color': INK, 'circle-stroke-opacity': 0.25, 'circle-stroke-width': 1 } },
    { id: 'you', type: 'circle', source: 'you', paint: { 'circle-radius': 6, 'circle-color': INK, 'circle-stroke-color': PAPER, 'circle-stroke-width': 2 } },
  ];
  for (const l of layers) map.addLayer(l);

  const setHover = (id, source = 'churches') => {
    if (api.hovered) map.setFeatureState(api.hovered, { hover: false });
    api.hovered = id == null ? null : { source, id };
    if (api.hovered) map.setFeatureState(api.hovered, { hover: true });
  };
  api.setHover = setHover;
  api.setSelected = (id) => {
    if (api.selected) map.setFeatureState({ source: 'churches', id: api.selected }, { selected: false });
    api.selected = id || null;
    if (api.selected) map.setFeatureState({ source: 'churches', id: api.selected }, { selected: true });
    map.setLayoutProperty('labels', 'symbol-sort-key', api.selected ? ['case', ['==', ['get', 'i'], api.selected], 0, ['get', 'pr']] : ['get', 'pr']);
  };

  let hoverId = null;
  map.on('mousemove', (e) => {
    const f = map.queryRenderedFeatures(e.point, { layers: ['points', 'clusters', 'grid'] })[0];
    map.getCanvas().style.cursor = f ? 'pointer' : '';
    const next = f ? f.id : null;
    if (next === hoverId) return;
    hoverId = next;
    if (!f) { setHover(null); emit('hover', null); return; }
    setHover(f.id, f.source);
    emit('hover', f, e.point);
  });
  map.on('mouseout', () => { hoverId = null; setHover(null); emit('hover', null); });
  map.on('click', (e) => {
    const f = map.queryRenderedFeatures(e.point, { layers: ['points', 'clusters', 'grid'] })[0];
    if (!f) { emit('clickEmpty'); return; }
    if (f.layer.id === 'points') emit('select', f.properties.i);
    else emit('cluster', f, e.point);
  });
  map.on('moveend', () => emit('moveend', map));

  api.setChurches = (matched, dimmed) => {
    map.getSource('churches').setData({ type: 'FeatureCollection', features: matched.map(toFeature) });
    map.getSource('dim').setData({ type: 'FeatureCollection', features: dimmed.map(toFeature) });
  };
  api.setGrid = (cells) => map.getSource('grid').setData({ type: 'FeatureCollection', features: cells.map(([lng, lat, n]) => ({ type: 'Feature', properties: { n }, geometry: { type: 'Point', coordinates: [lng, lat] } })) });
  api.setYou = (pos) => map.getSource('you').setData(pos ? { type: 'FeatureCollection', features: [{ type: 'Feature', properties: { px: Math.min(60, Math.max(8, pos.accuracy / 40)) }, geometry: { type: 'Point', coordinates: [pos.lng, pos.lat] } }] } : EMPTY);
  api.setNeighborhoods = (geojson) => map.getSource('nb').setData(geojson || EMPTY);
  let activeNb = null;
  api.setActiveNeighborhood = (slug) => {
    if (activeNb) map.setFeatureState({ source: 'nb', id: activeNb }, { active: false });
    activeNb = slug || null;
    if (activeNb) map.setFeatureState({ source: 'nb', id: activeNb }, { active: true });
  };
  api.setLabels = (on) => { api.labels = !!on; map.setLayoutProperty('labels', 'visibility', on ? 'visible' : 'none'); };
  api.setHeat = (on) => { api.heat = !!on; map.setLayoutProperty('heat', 'visibility', on ? 'visible' : 'none'); };
  api.clusterLeaves = (f) => new Promise((resolve) => map.getSource('churches').getClusterLeaves(f.properties.cluster_id, 1000, 0, (err, leaves) => resolve(err ? [] : leaves)));
  api.expandCluster = (f) => new Promise((resolve) => map.getSource('churches').getClusterExpansionZoom(f.properties.cluster_id, (err, z) => resolve(err ? map.getZoom() + 2 : z)));
}

/** Padding that keeps the panel and the drawer off the subject. */
export function padding(panelOpen, drawerOpen, mobile) {
  if (mobile) return { top: 70, bottom: 190, left: 16, right: 16 };
  return { top: 60, bottom: 40, left: panelOpen ? 380 : 40, right: drawerOpen ? 440 : 40 };
}
